import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { useTranslation } from 'react-i18next'

type ToastKind = 'success' | 'error' | 'info'

interface Toast {
  id: number
  kind: ToastKind
  message: string
}

export interface ToastController {
  success: (message: string) => void
  error: (message: string) => void
  info: (message: string) => void
}

const ToastContext = createContext<ToastController | undefined>(undefined)

const MAX_VISIBLE = 4
const AUTO_HIDE_MS = 5000

let nextId = 0

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  // Keep a map of timers so we can clear on manual dismiss
  const timers = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map())

  const dismiss = useCallback((id: number) => {
    const t = timers.current.get(id)
    if (t !== undefined) {
      clearTimeout(t)
      timers.current.delete(id)
    }
    setToasts((prev) => prev.filter((toast) => toast.id !== id))
  }, [])

  const add = useCallback(
    (kind: ToastKind, message: string) => {
      const id = nextId++
      setToasts((prev) => {
        const next = [...prev, { id, kind, message }]
        // Keep only the last MAX_VISIBLE toasts
        return next.slice(-MAX_VISIBLE)
      })
      const timer = setTimeout(() => {
        dismiss(id)
      }, AUTO_HIDE_MS)
      timers.current.set(id, timer)
    },
    [dismiss]
  )

  // Clear all timers on unmount
  useEffect(() => {
    return () => {
      timers.current.forEach((t) => clearTimeout(t))
    }
  }, [])

  // Stable identity: `add` is a stable useCallback, so the controller is
  // created once and useToast consumers never re-render on toast changes.
  const controller: ToastController = useMemo(
    () => ({
      success: (message) => add('success', message),
      error: (message) => add('error', message),
      info: (message) => add('info', message),
    }),
    [add]
  )

  return (
    <ToastContext.Provider value={controller}>
      {children}
      {/* Toast stack: bottom-right, newest appended at the bottom */}
      <div
        aria-live="polite"
        className="pointer-events-none fixed bottom-4 right-4 z-50 flex flex-col gap-2"
      >
        {toasts.map((toast) => (
          <ToastItem key={toast.id} toast={toast} onDismiss={dismiss} />
        ))}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastController {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast must be used within a ToastProvider')
  return ctx
}

// ────────────────────────────────────────────────────────────────────────────

interface ToastItemProps {
  toast: Toast
  onDismiss: (id: number) => void
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const { id, kind, message } = toast
  // The `message` arrives already translated from whichever caller raised the
  // toast; the × is this component's own chrome, so it owns its own key.
  const { t } = useTranslation()

  const colorClasses =
    kind === 'error'
      ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950 dark:border-red-800 dark:text-red-200'
      : kind === 'success'
        ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-950 dark:border-green-800 dark:text-green-200'
        : 'bg-stone-50 border-stone-200 text-stone-800 dark:bg-stone-800 dark:border-stone-700 dark:text-stone-200'

  return (
    <div
      role={kind === 'error' ? 'alert' : 'status'}
      className={`pointer-events-auto flex items-center justify-between gap-3 rounded-lg border px-4 py-2.5 shadow-md text-sm ${colorClasses}`}
    >
      <span>{message}</span>
      <button
        data-testid={`toast-dismiss-${id}`}
        type="button"
        aria-label={t('toaster.dismiss')}
        onClick={() => onDismiss(id)}
        className="shrink-0 text-current opacity-60 hover:opacity-100"
      >
        ×
      </button>
    </div>
  )
}
